// src/components/CommonComponents/ProtectedRoute.js
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LockKeyhole, LogIn, ShieldAlert } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { authStorage } from "../utils/authStorage";
import { resolveSessionLoginPath } from "../utils/sessionExpiredEvents";

const AccessDeniedScreen = ({ onLogout }) => (
    <main
        className="min-h-screen px-4 py-10 flex items-center justify-center"
        style={{
            background:
                "radial-gradient(circle at 50% 18%, color-mix(in srgb, var(--color-primary) 10%, transparent), transparent 34%), #f8fafc",
        }}
    >
        <section
            role="alert"
            aria-live="polite"
            className="w-full max-w-[520px] overflow-hidden rounded-[28px] border border-slate-200/90 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.14)]"
        >
            <div
                className="h-2 w-full"
                style={{
                    background:
                        "linear-gradient(90deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 58%, #ffffff))",
                }}
            />
            <div className="px-6 py-8 text-center sm:px-9 sm:py-10">
                <div
                    className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-white shadow-lg"
                    style={{
                        background: "var(--color-primary)",
                        boxShadow: "0 18px 40px color-mix(in srgb, var(--color-primary) 24%, transparent)",
                    }}
                >
                    <ShieldAlert className="h-8 w-8" aria-hidden="true" />
                </div>

                <p
                    className="mt-6 text-xs font-bold uppercase tracking-[0.28em]"
                    style={{ color: "var(--color-primary)" }}
                >
                    Access Restricted
                </p>
                <h1 className="mt-3 text-3xl font-bold text-slate-950">
                    Access denied
                </h1>
                <p className="mx-auto mt-3 max-w-[390px] text-base leading-7 text-slate-600">
                    This account does not have permission to open this workspace area.
                </p>

                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left">
                    <div className="flex gap-3">
                        <LockKeyhole
                            className="mt-0.5 h-5 w-5 shrink-0"
                            aria-hidden="true"
                            style={{ color: "var(--color-primary)" }}
                        />
                        <p className="text-sm leading-6 text-slate-600">
                            You may be signed in with the wrong role or workspace account.
                            Sign in again using the correct account to continue.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => onLogout()}
                    className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-5 py-4 text-base font-semibold text-white shadow-lg transition duration-200 hover:brightness-95 active:scale-[0.99]"
                    style={{
                        background: "var(--color-primary)",
                        boxShadow: "0 18px 40px color-mix(in srgb, var(--color-primary) 22%, transparent)",
                    }}
                >
                    <LogIn className="h-5 w-5" aria-hidden="true" />
                    Logout &amp; Login Again
                </button>
            </div>
        </section>
    </main>
);

const ProtectedRoute = ({ children, allowedRoles = [], disallowManager = false }) => {
    const location = useLocation();
    const { isAuthenticated, hasRole, logout, isRehydrated } = useAuth();

    // Wait for rehydration before checking auth
    if (!isRehydrated) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <span className="ml-4 text-lg">Loading...</span>
            </div>
        );
    }

    // Not logged in → redirect to login
    if (!isAuthenticated()) {
        return <Navigate to={resolveSessionLoginPath(location.pathname)} replace state={{ from: location }} />;
    }

    const loginContext = authStorage.getLoginContext();
    const contextMismatch = loginContext === "admin" && allowedRoles.includes(2)
        ? true
        : loginContext === "pos" && allowedRoles.includes(1)
            ? true
            : false;

    const user = authStorage.getUser();
    const isManager = user?.user_type === 2
        && String(user?.role_name || "").toLowerCase() === "manager";
    const isAdminSubscriptionReturn = loginContext === "admin"
        && allowedRoles.includes(2)
        && new URLSearchParams(location.search).get("subscription_provider") === "hitpay";

    if (isAdminSubscriptionReturn) {
        return <Navigate to={`/admin/dashboard${location.search}`} replace />;
    }

    // Logged in but role not allowed
    if ((allowedRoles.length && !hasRole(allowedRoles)) || contextMismatch || (disallowManager && isManager)) {
        return <AccessDeniedScreen onLogout={logout} />;
    }

    // Authorized → render children
    return children;
};

export default ProtectedRoute;
